import { useCallback, useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";

import { API_GET_PROJECT_UTILIZATION_REPORT } from "../../../config/Endpoints.js";
import type { PeriodRange } from "../../components/features/time-allocated-overview/types.ts";
import type { ProjectUtilizationReport } from "../../components/features/project-reports/types.ts";
import { postRawJson } from "../../utils/api.ts";
import { periodRangeToApiDates } from "../../utils/period-range.ts";

function unwrapProjectReport(raw: unknown): ProjectUtilizationReport {
  if (
    raw &&
    typeof raw === "object" &&
    "data" in raw &&
    (raw as { data?: unknown }).data != null
  ) {
    return (raw as { data: ProjectUtilizationReport }).data;
  }
  return raw as ProjectUtilizationReport;
}

export function useProjectUtilizationReport(
  projectId: string,
  range: PeriodRange,
) {
  const [data, setData] = useState<ProjectUtilizationReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rangeKey = useMemo(() => JSON.stringify(range), [range]);
  const accessToken = Cookies.get("accessToken");

  const fetchReport = useCallback(
    async (options?: { signal?: AbortSignal }) => {
      const signal = options?.signal;
      const trimmedProjectId = projectId.trim();
      const dates = periodRangeToApiDates(JSON.parse(rangeKey) as PeriodRange);

      if (!trimmedProjectId || !dates) {
        setData(null);
        setLoading(false);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await postRawJson<unknown>(
          API_GET_PROJECT_UTILIZATION_REPORT,
          accessToken,
          {
            projectId: Number(trimmedProjectId),
            fromDate: dates.from,
            toDate: dates.to,
          },
          signal,
        );
        if (signal?.aborted) return;
        setData(unwrapProjectReport(response.data));
      } catch (e) {
        if (e instanceof Error && e.name === "AbortError") return;
        if (signal?.aborted) return;
        setError(e instanceof Error ? e.message : "Failed to load report");
        setData(null);
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [accessToken, projectId, rangeKey],
  );

  useEffect(() => {
    const controller = new AbortController();
    void fetchReport({ signal: controller.signal });
    return () => controller.abort();
  }, [fetchReport]);

  return { data, loading, error, refetch: () => void fetchReport() };
}
